import React from 'react';
import { getContrastColor } from '../../../utils';
import { bo } from '@fullcalendar/core/internal-common';

type ChipProps = {
  bgColor?: string;
  textColor?: string;
  label: string;
  borderRadius?: string;
};

export const Chip = ({ bgColor, textColor, label, borderRadius }: ChipProps) => {
  return (
    <span
      className="px-2"
      style={{
        backgroundColor: bgColor,
        color: textColor,
        borderRadius: borderRadius || '0px',
        fontSize: '0.875rem',
        lineHeight: '1.5',
        display: 'inline-block',
      }}
    >
      {label}
    </span>
  );
};

// StatusOption type removed — components accept any option shape

/**
 * React-Select compatible Option component.
 * Use it via the `components` prop: `{ Option: SelectOption }`.
 */
export const SelectOption: React.FC<any> = (props: any) => {
  const { innerRef, innerProps, data, isFocused } = props as any;
  const hex = data?.colorCode ?? '#dddddd';
  const translucent = /^#([0-9A-Fa-f]{6})$/.test(hex) ? `${hex}80` : hex;
  // Normalize `isSystem` which may be boolean, number or string from the API
  const isSystem = [true, 'true', 1, '1'].includes(data?.isSystem);

  const editHandler = data?.onEdit;
  const deleteHandler = data?.onDelete;
  return (
    <div
      ref={innerRef}
      {...innerProps}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '6px 8px',
        // only show translucent background when focused/hovered
        background: isFocused ? translucent : 'transparent',
      }}
    >
      <Chip bgColor={translucent} textColor={'inherit'} label={data.label} borderRadius="2px" />
      {!isSystem && editHandler && deleteHandler && (
        <div style={{ display: 'flex', gap: 8, marginLeft: 8 }}>
          <button
            type="button"
            aria-label={`edit-${data.value}`}
            style={{
              border: `1px solid rgba(0, 0, 0, 0.4)`,
              borderRadius: '2px',
              background: 'transparent',
              cursor: 'pointer',
            }}
            onClick={editHandler}
          >
            <i className="fa fa-pencil"></i>
          </button>
          <button
            type="button"
            aria-label={`delete-${data.value}`}
            style={{
              border: `1px solid rgba(0, 0, 0, 0.4)`,
              borderRadius: '2px',
              background: 'transparent',
              cursor: 'pointer',
            }}
            onClick={deleteHandler}
          >
            <i className="fa fa-trash-o"></i>
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * React-Select MultiValue component to render selected chips.
 * Use via `components={{ MultiValue: MultiValueContainer }}` on a multi Select.
 */
export const MultiValueContainer: React.FC<any> = (props: any) => {
  const { data, innerProps, removeProps } = props as any;
  const hex = data?.colorCode ?? '#dddddd';
  const translucent = /^#([0-9A-Fa-f]{6})$/.test(hex) ? `${hex}80` : hex;

  return (
    <div
      {...innerProps}
      style={{
        display: 'inline-flex',
        marginRight: 2,
        alignItems: 'center',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Chip bgColor={translucent} textColor={'inherit'} label={data.label} borderRadius="0px" />
      <div
        {...removeProps}
        style={{
          cursor: 'pointer',
          paddingRight: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: translucent,
          color: 'inherit',
        }}
        aria-label={`Remove ${data.label}`}
      >
        ×
      </div>
    </div>
  );
};

/**
 * React-Select SingleValue component to render the selected value when `isMulti=false`.
 * Use via `components={{ SingleValue: SingleValueContainer }}`.
 */
export const SingleValueContainer: React.FC<any> = (props: any) => {
  const { data, innerProps } = props as any;
  const hex = data?.colorCode ?? '#dddddd';
  const translucent = /^#([0-9A-Fa-f]{6})$/.test(hex) ? `${hex}80` : hex;

  return (
    <div
      {...innerProps}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        overflow: 'hidden',
        flex: '0 1 auto',
        // ensure the singleValue spans the full value container when parent uses grid
        gridColumn: '1 / -1',
        position: 'absolute',
      }}
    >
      <Chip bgColor={translucent} textColor={'inherit'} label={data.label} borderRadius="2px" />
    </div>
  );
};
